import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { hasModule, ROLE_LABELS, can } from "../lib/rbac";
import api from "../lib/api";
import { ShoppingCart, Wallet, HeartHandshake, LogOut, ArrowRight, Lock, Users, ClipboardCheck, Clock } from "lucide-react";

const MODULES = [
    {
        key: "purchasing",
        title: "Satın Alma",
        description: "Talep, teklif, sipariş, mal kabul ve fatura süreçlerini uçtan uca yönetin.",
        Icon: ShoppingCart,
        module: "purchasing",
        path: "/",
        stats: "P2P Workflow • 3'lü Eşleştirme",
    },
    {
        key: "cash",
        title: "Kasa",
        description: "Nakit akışı, tahsilat, tediye ve banka mutabakat süreçleri.",
        Icon: Wallet,
        module: "cash",
        path: null,
        comingSoon: true,
        stats: "Yakında",
    },
    {
        key: "hr",
        title: "İK / İSG",
        description: "Personel yönetimi, izin, bordro ve iş sağlığı & güvenliği modülleri.",
        Icon: HeartHandshake,
        module: "hr",
        path: null,
        comingSoon: true,
        stats: "Yakında",
    },
];

export default function Portal() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const canApprove = can(user, "pr.approve");
    const [pending, setPending] = useState(null); // { count, oldest_wait_days }

    useEffect(() => {
        if (!canApprove) return;
        api.get("/purchase-requests/summary/pending-approvals")
            .then((r) => setPending(r.data))
            .catch(() => setPending({ count: 0, oldest_wait_days: null }));
    }, [canApprove]);

    const onLogout = async () => {
        await logout();
        nav("/login");
    };

    return (
        <div
            className="min-h-screen text-white relative overflow-hidden"
            style={{ background: "linear-gradient(180deg, hsl(220 90% 20%) 0%, hsl(222 30% 8%) 100%)" }}
            data-testid="portal-page"
        >
            {/* Grid overlay */}
            <div
                className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                }}
            />

            {/* Top bar */}
            <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
                <div className="inline-flex items-center bg-white rounded-lg px-4 py-2 shadow-md">
                    <img
                        src="/brand/eyes-celik-logo-trimmed.webp"
                        alt="Eyes Çelik"
                        className="h-10 w-auto block"
                        data-testid="portal-brand-logo"
                    />
                </div>
                <div className="flex items-center gap-4">
                    {user && user.email && (
                        <div className="hidden sm:flex flex-col items-end">
                            <div className="text-sm font-medium" data-testid="portal-user-email">
                                {user.email}
                            </div>
                            <div className="text-[11px] uppercase tracking-widest text-white/50">
                                {ROLE_LABELS[user.role] || user.role || "user"}
                            </div>
                        </div>
                    )}
                    <button
                        onClick={onLogout}
                        data-testid="portal-logout-btn"
                        className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white border border-white/15 hover:border-white/40 rounded-md px-3 py-2 transition-colors"
                    >
                        <LogOut size={15} />
                        Çıkış
                    </button>
                </div>
            </header>

            {/* Hero */}
            <section className="relative z-10 px-6 md:px-12 pt-8 md:pt-16 pb-8 max-w-6xl mx-auto">
                <div className="text-[11px] uppercase tracking-widest font-medium mb-3" style={{ color: "hsl(174 72% 60%)" }}>
                    Eyes Çelik Yönetim Portalı
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1] max-w-3xl">
                    Tüm iş süreçlerinizi<br />tek portalda yönetin.
                </h1>
                <p className="text-white/60 mt-6 text-[15px] md:text-base max-w-2xl leading-relaxed">
                    Devam etmek istediğiniz modülü seçin. Yeni modüller kısa süre içinde eklenecektir.
                </p>
            </section>

            {/* Module cards */}
            <section className="relative z-10 px-6 md:px-12 pb-16 max-w-6xl mx-auto">
                {/* Pending approvals — only for managers/admins with pr.approve */}
                {canApprove && pending && (
                    <div
                        data-testid="portal-pending-approvals-card"
                        className="mb-6 rounded-xl border p-5 md:p-6 flex items-center justify-between gap-4 flex-wrap"
                        style={{
                            background: pending.count > 0
                                ? "linear-gradient(90deg, hsl(38 92% 20% / 0.35) 0%, hsl(220 30% 12%) 100%)"
                                : "hsl(220 30% 10% / 0.6)",
                            borderColor: pending.count > 0 ? "hsl(38 92% 55% / 0.5)" : "hsl(0 0% 100% / 0.08)",
                        }}
                    >
                        <div className="flex items-center gap-4">
                            <div
                                className="w-12 h-12 rounded-lg flex items-center justify-center"
                                style={{
                                    background: pending.count > 0
                                        ? "linear-gradient(135deg, hsl(38 92% 55%) 0%, hsl(28 90% 45%) 100%)"
                                        : "hsl(220 20% 20%)",
                                }}
                            >
                                <ClipboardCheck size={22} className="text-white" />
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-widest text-white/50 mb-1">
                                    Bekleyen Onaylar
                                </div>
                                <div className="flex items-baseline gap-3">
                                    <span
                                        className="text-3xl font-semibold tabular-nums"
                                        data-testid="pending-approvals-count"
                                    >
                                        {pending.count}
                                    </span>
                                    <span className="text-sm text-white/70">
                                        {pending.count > 0 ? "onayınızı bekleyen talep" : "bekleyen talep yok"}
                                    </span>
                                </div>
                                {pending.count > 0 && user?.role !== "admin" && (
                                    <div className="text-[10px] text-white/40 mt-0.5">
                                        Kendi oluşturduğunuz talepler hariç
                                    </div>
                                )}
                                {pending.count > 0 && pending.oldest_wait_days !== null && (
                                    <div
                                        className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-amber-200"
                                        data-testid="pending-approvals-oldest"
                                    >
                                        <Clock size={12} />
                                        En eski talep {pending.oldest_wait_days === 0 ? "bugün" : `${pending.oldest_wait_days} gündür`} bekliyor
                                    </div>
                                )}
                            </div>
                        </div>
                        {pending.count > 0 && (
                            <button
                                data-testid="pending-approvals-go-btn"
                                onClick={() => nav("/purchase-requests?status=waiting_approval")}
                                className="inline-flex items-center gap-2 text-sm bg-white text-slate-900 hover:bg-white/90 rounded-md px-4 py-2.5 font-medium transition-colors"
                            >
                                Onaylara Git
                                <ArrowRight size={14} />
                            </button>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                    {MODULES.map(({ key, title, description, Icon, module, path, comingSoon, stats }) => {
                        const userHasAccess = hasModule(user, module);
                        const available = userHasAccess && !comingSoon;
                        const noAccess = !userHasAccess && !comingSoon;
                        // Requester goes straight to /purchase-requests (no dashboard KPI leak)
                        const targetPath = (key === "purchasing" && user?.role === "requester") ? "/purchase-requests" : path;
                        const cardBase =
                            "relative rounded-xl border p-6 md:p-7 transition-all duration-200 flex flex-col min-h-[240px]";
                        const active =
                            "bg-white/[0.04] border-white/10 hover:bg-white/[0.08] hover:border-[hsl(174_72%_45%)] cursor-pointer hover:-translate-y-0.5";
                        const disabled =
                            "bg-white/[0.02] border-white/5 cursor-not-allowed opacity-60";

                        const cardProps = available
                            ? {
                                  onClick: () => nav(targetPath),
                                  role: "button",
                                  tabIndex: 0,
                                  onKeyDown: (e) => {
                                      if (e.key === "Enter" || e.key === " ") nav(targetPath);
                                  },
                              }
                            : { "aria-disabled": true };

                        const badgeLabel = comingSoon ? "Yakında" : (noAccess ? "Yetki Yok" : null);

                        return (
                            <div
                                key={key}
                                data-testid={`portal-module-${key}`}
                                data-available={available ? "true" : "false"}
                                className={`${cardBase} ${available ? active : disabled}`}
                                {...cardProps}
                            >
                                <div className="flex items-start justify-between mb-6">
                                    <div
                                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                                        style={{
                                            background: available
                                                ? "linear-gradient(135deg, hsl(174 72% 45%) 0%, hsl(174 72% 30%) 100%)"
                                                : "hsl(220 20% 20%)",
                                        }}
                                    >
                                        <Icon size={22} className="text-white" />
                                    </div>
                                    {badgeLabel && (
                                        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/50 border border-white/10 rounded-full px-2.5 py-1">
                                            <Lock size={10} />
                                            {badgeLabel}
                                        </div>
                                    )}
                                </div>
                                <div className="text-xl font-semibold tracking-tight mb-2">{title}</div>
                                <p className="text-white/60 text-sm leading-relaxed flex-1">{description}</p>
                                <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
                                    <span
                                        className="text-[11px] uppercase tracking-widest"
                                        style={{
                                            color: available ? "hsl(174 72% 60%)" : "hsl(0 0% 60%)",
                                        }}
                                    >
                                        {noAccess ? "Erişim izniniz yok" : stats}
                                    </span>
                                    {available && (
                                        <ArrowRight size={16} className="text-white/70" />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {user?.role === "admin" && (
                    <div className="mt-8 pt-6 border-t border-white/10">
                        <div className="text-[11px] uppercase tracking-widest text-white/40 mb-3">
                            Yönetim
                        </div>
                        <button
                            data-testid="portal-user-management-btn"
                            onClick={() => nav("/admin/users")}
                            className="inline-flex items-center gap-3 text-sm text-white/80 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-[hsl(174_72%_45%)] rounded-lg px-4 py-3 transition-all"
                        >
                            <Users size={16} />
                            Kullanıcı Yönetimi
                            <ArrowRight size={14} className="opacity-60" />
                        </button>
                    </div>
                )}
            </section>

            {/* Footer */}
            <footer className="relative z-10 px-6 md:px-12 py-6 text-xs text-white/40 border-t border-white/5 max-w-6xl mx-auto">
                © 2026 Eyes Çelik • Yönetim Portalı
            </footer>
        </div>
    );
}
